import { DownloadSimpleIcon, FileArrowDownIcon } from '@phosphor-icons/react/dist/ssr'
import Image from 'next/image'

import { AspectRatio } from '@/components/primitives/AspectRatio/AspectRatio'
import { ButtonLink } from '@/components/primitives/Button/Button'
import { Card as CardUI, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { cn, getImageUrl } from '@/lib/utils'

import type { ResourceCardType } from '@/types/contentful/resourceSectionType'
import type { ReactNode } from 'react'

type ResourceCardProps = {
  title: string
  CTALink: string
  CTALabel: string
  containerClass?: string
  CTAIcon?: ReactNode
}

export type ResourceCardDefaultProps = ResourceCardProps & {
  category: string
  description: string
}

export const ResourceCardDefault = ({ category, title, CTALink, description, CTALabel, containerClass, CTAIcon }: ResourceCardDefaultProps) => {
  if (!category && !title && !CTALabel) return null

  return (
    <CardUI className={cn('flex w-full max-w-[368px] flex-col justify-between rounded-2xl border-grey-200 bg-white p-6 shadow-md md:p-12', containerClass)}>
      <div>
        <CardHeader className="mb-4 space-y-2 p-0">
          {category && <div className="text-base font-bold text-primary-500">{category}</div>}
          {title && <CardTitle className="text-start text-2xl font-semibold leading-[30px] text-grey-900 ">{title}</CardTitle>}
        </CardHeader>
        {description && (
          <CardContent className="gap-1 p-0">
            <CardDescription className="text-base text-grey-700">{description}</CardDescription>
          </CardContent>
        )}
      </div>
      {CTALabel && (
        <CardFooter className="mt-6 flex items-start p-0">
          <ButtonLink variant="outline" href={CTALink ?? ''} className="gap-2">
            {CTAIcon}
            {CTALabel}
          </ButtonLink>
        </CardFooter>
      )}
    </CardUI>
  )
}

export const ResourceCardWithImage = ({ image, title, downloadLink }: Partial<ResourceCardType['fields']>) => {
  return (
    <CardUI className="flex size-full max-w-[344px] flex-1 flex-col items-center justify-center gap-6 rounded-2xl border-grey-200 bg-white p-6 shadow-sm sm:max-w-[276px] sm:gap-8 sm:p-8">
      {title && (
        <CardHeader className="hidden gap-4 p-0 sm:block">
          <CardTitle className="max-h-[56px] text-center text-xl font-semibold leading-6 text-primary-500">{title}</CardTitle>
        </CardHeader>
      )}
      <CardContent className="flex w-full flex-row items-center gap-6 p-0 sm:flex-col sm:gap-8">
        <div className="flex w-full min-w-[100px] max-w-[153px] items-center justify-center">
          <AspectRatio ratio={'3/4'}>
            {!!image ? (
              <Image alt={image?.fields?.title ?? 'Image'} src={getImageUrl(image)} width={152} height={215} className="m-0 size-full rounded object-fill p-0 shadow-md" />
            ) : (
              <div className="flex size-full"></div>
            )}
          </AspectRatio>
        </div>
        <div className="flex w-full flex-col items-start gap-4 p-0">
          {title && <CardTitle className="block text-left text-xl font-semibold leading-6 text-primary-500 sm:hidden md:text-center">{title}</CardTitle>}{' '}
          {downloadLink && (
            <ButtonLink variant="outline" href={downloadLink} className="w-full gap-2">
              <DownloadSimpleIcon weight="bold" className="size-5" /> Download
            </ButtonLink>
          )}
        </div>
      </CardContent>
    </CardUI>
  )
}

export const ResourceCardWithIcon = ({ title, downloadLink }: Partial<ResourceCardType['fields']>) => {
  return (
    <CardUI className="flex w-full flex-1 flex-col items-center justify-center gap-6 rounded-2xl border-grey-200 bg-white p-6 shadow-sm sm:gap-8 sm:p-12">
      <CardContent className="flex w-full flex-row items-start gap-6 p-0 sm:gap-8">
        <div className="rounded-2xl border border-grey-200 bg-grey-100 p-7">
          <AspectRatio ratio={'1/1'}>
            <FileArrowDownIcon size={40} className="fill-primary-500" />
          </AspectRatio>
        </div>
        <div className="flex w-full flex-col items-start gap-6 p-0">
          {title && <CardTitle className="block text-left text-xl font-semibold leading-6 text-primary-500">{title}</CardTitle>}
          {downloadLink && (
            <ButtonLink variant="outline" href={downloadLink} className="gap-2">
              <DownloadSimpleIcon weight="bold" className="size-5" /> Download
            </ButtonLink>
          )}
        </div>
      </CardContent>
    </CardUI>
  )
}
